import React from 'react';

/**
 * Renders the structured Phase-A listing-field proposal produced by the pipeline
 * (stage 05 / extract_listing_fields) and carried on a listingEnrichmentRun in
 * `stages['05_listing_fields'].proposalData`. Shape:
 *
 *   { ownerControlled: bool,
 *     fills:      ["contact.phone", "address.city", ...],     // empty → value
 *     overwrites: [{ field, old, new }],                      // first-hand replaces stale
 *     flags:      [{ type, field, old, new, action? }],       // needs manual review
 *     autoPatch:  { contact: {...}, address: {...}, ... } }   // what Approve applies
 *
 * Approve (on the run) applies `autoPatch` (fills + overwrites). Flags are
 * surfaced read-only — name/address/geo are never auto-changed.
 */

// Walk a dotted path ("contact.phone") into the autoPatch to show the new value.
function valueAt(obj, path) {
  return path.split('.').reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);
}

function fmt(v) {
  if (v === undefined || v === null || v === '') return <span className="text-gray-400 italic">empty</span>;
  if (typeof v === 'object') return <code className="break-all">{JSON.stringify(v)}</code>;
  return <span className="break-all">{String(v)}</span>;
}

const FLAG_LABEL = {
  name_mismatch: 'Name mismatch',
  address_conflict: 'Address conflict',
  owner_propose: 'Owner-controlled (propose only)',
};

function DiffRow({ field, from, to, tone }) {
  const toneCls = {
    fill: 'bg-green-50 border-green-200',
    overwrite: 'bg-amber-50 border-amber-200',
    flag: 'bg-yellow-50 border-yellow-200',
  }[tone] || 'bg-gray-50 border-gray-200';
  return (
    <div className={`text-xs border rounded px-2 py-1 ${toneCls}`}>
      <span className="font-mono text-gray-700">{field}</span>
      <span className="mx-1 text-gray-400">:</span>
      {tone !== 'fill' && (<>{fmt(from)}<span className="mx-1 text-gray-400">→</span></>)}
      {fmt(to)}
    </div>
  );
}

export default function ListingFieldChanges({ proposalData }) {
  if (!proposalData) return null;
  const {
    ownerControlled = false,
    fills = [],
    overwrites = [],
    flags = [],
    autoPatch = {},
  } = proposalData;

  const nothing = !fills.length && !overwrites.length && !flags.length;

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <h4 className="text-xs uppercase tracking-wide text-gray-500">Listing field changes (Phase A)</h4>
        {ownerControlled && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
            owner-controlled · propose only
          </span>
        )}
      </div>

      {nothing && (
        <div className="text-xs text-gray-400 italic">No listing-field changes proposed.</div>
      )}

      {fills.length > 0 && (
        <div className="mb-2">
          <div className="text-[11px] font-medium text-green-700 mb-1">Fills · empty → value (applied on approve)</div>
          <div className="space-y-1">
            {fills.map((f) => <DiffRow key={f} field={f} to={valueAt(autoPatch, f)} tone="fill" />)}
          </div>
        </div>
      )}

      {overwrites.length > 0 && (
        <div className="mb-2">
          <div className="text-[11px] font-medium text-amber-700 mb-1">Overwrites · first-hand replaces stale (applied on approve)</div>
          <div className="space-y-1">
            {overwrites.map((o, i) => <DiffRow key={i} field={o.field} from={o.old} to={o.new} tone="overwrite" />)}
          </div>
        </div>
      )}

      {flags.length > 0 && (
        <div className="mb-1">
          <div className="text-[11px] font-medium text-yellow-700 mb-1">Flags · needs manual review (NOT auto-applied)</div>
          <div className="space-y-1">
            {flags.map((f, i) => (
              <DiffRow
                key={i}
                field={`${FLAG_LABEL[f.type] || f.type}${f.field ? ` (${f.field})` : ''}`}
                from={f.old}
                to={f.new}
                tone="flag"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
